import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { Button } from '@/components/base/Button';
import { PromotionalCard } from '@/components/sections/PromotionalCard/PromotionalCard';
import { HelpfulContactsCard } from '@/components/sections/HelpfulContactsCard/HelpfulContactsCard';
import type { PromotionalCardContent } from '@/components/sections/PromotionalCard/PromotionalCard';
import type { HelpfulContactsCardContent } from '@/components/sections/HelpfulContactsCard/HelpfulContactsCard';

/**
 * Content for the feedback prompt section (heading and description).
 * The actual feedback is collected via an external SurveyMonkey link.
 */
export interface FeedbackPromptContent {
  /** Heading text (e.g., "Did you find what you were looking for?") */
  heading: string;
  /** Description text (e.g., "Fill out this form to let us know about your experience.") */
  description: string;
}

/**
 * Content interface for SearchRightPane component.
 * Composes content for three child components displayed in a sidebar stack.
 */
export interface SearchRightPaneContent {
  /** Content for the promotional card (Find Services) */
  promotionalCard: PromotionalCardContent;
  /** Content for the helpful contacts card */
  helpfulContacts: HelpfulContactsCardContent;
  /** Content for the feedback prompt (heading and description only; links to SurveyMonkey) */
  feedbackForm: FeedbackPromptContent;
}

/**
 * Props interface for SearchRightPane component.
 */
export interface SearchRightPaneProps {
  /** Content for all sidebar child components. If undefined, renders nothing. */
  content?: SearchRightPaneContent;
  /** @deprecated No longer used. Feedback now links to external SurveyMonkey survey. */
  onFeedbackSubmit?: (...args: unknown[]) => void;
  /** Whether font enlargement is active */
  isFontEnlarged?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SearchRightPane - Sidebar column for the Search Results page.
 *
 * Composes three sections in a vertical stack with 24px gap:
 * 1. PromotionalCard (Find services in your area)
 * 2. HelpfulContactsCard (phone contacts)
 * 3. Feedback prompt with external SurveyMonkey link
 *
 * Displayed to the right of search results on desktop, and below on mobile.
 * Visible in all page states (empty, results, no results).
 *
 * @example
 * ```tsx
 * import { SearchRightPane } from '@/components';
 *
 * <SearchRightPane
 *   content={searchRightPaneContent}
 * />
 * ```
 */
export function SearchRightPane({
  content,
  onFeedbackSubmit: _onFeedbackSubmit,
  isFontEnlarged = false,
  className,
}: SearchRightPaneProps) {
  if (!content) {
    return null;
  }

  return (
    <aside
      role="complementary"
      aria-label="Supplementary information"
      className={cn(
        'flex flex-col gap-6 w-full',
        className
      )}
      data-testid="search-right-pane"
    >
      <PromotionalCard
        content={content.promotionalCard}
        variant="compact"
        isFontEnlarged={isFontEnlarged}
        headingLevel="h2"
      />
      <HelpfulContactsCard
        content={content.helpfulContacts}
        isFontEnlarged={isFontEnlarged}
        headingLevel="h2"
      />
      <div className="rounded border border-[#EBEBEB] bg-white p-4 shadow-[0px_4px_4px_0px_#BBBBBB40]">
        <h2 className={cn(typography.headingSmall, 'text-black')}>
          {content.feedbackForm.heading}
        </h2>
        <p className={cn(typography.body, 'text-black mt-4')}>
          {content.feedbackForm.description}
        </p>
        <Button
          className="mt-4"
          variant="secondary"
          size="medium"
          content={{
            text: 'Open form',
            icon: 'ArrowRight',
          }}
          onClick={() => window.open('https://www.surveymonkey.com/r/FeedbackForResourceHub', '_blank', 'noopener,noreferrer')}
          aria-label="Open form"
        />
      </div>
    </aside>
  );
}

SearchRightPane.displayName = 'SearchRightPane';
